import { useEffect, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { useApiClient } from "./useApiClient";

// Fires POST /api/me/heartbeat once per authenticated session start so the backend can record a
// UserLoginEvent row. The server itself dedupes within a 30-min window, so this client-side
// guard (runOnce ref + sessionStorage flag) is belt-and-braces: it also avoids hammering the
// endpoint on every React re-mount during a single tab's lifetime.
//
// Call from the root authenticated view — App.tsx renders the Layout inside <AuthenticatedTemplate>
// and we drop <LoginHeartbeat /> there.
export function useLoginHeartbeat() {
  const { accounts } = useMsal();
  const { request } = useApiClient();
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    if (accounts.length === 0) return;
    // Tab-level guard: if we already fired in this tab, skip (survives page navigation but not
    // a new tab — which is what we want: a new tab is a meaningful new session).
    if (sessionStorage.getItem("heartbeat_sent") === "1") {
      fired.current = true;
      return;
    }
    fired.current = true;
    request<void>("/me/heartbeat", { method: "POST" })
      .then(() => sessionStorage.setItem("heartbeat_sent", "1"))
      .catch(() => {
        // Silent failure — heartbeat is audit-only, not blocking. Reset the ref so a reload can
        // retry next time.
        fired.current = false;
      });
  }, [accounts, request]);
}
